(function() {
  const STORAGE_KEY_PREFIX = 'pamuuc:favorites:v2:';
  const LEGACY_STORAGE_KEY = 'pamuuc:favorites:v1';
  const MAX_ITEMS = 48;

  class FavoritesManager {
    constructor() {
      this.drawer = document.querySelector('.js-favorites-drawer');
      if (!this.drawer) return;

      this.isCustomerLoggedIn = this.drawer.dataset.customerLoggedIn === 'true';
      this.customerKey = String(this.drawer.dataset.customerKey || '').trim();
      this.storageKey = this.getStorageKey();
      this.overlay = document.querySelector('.js-overlay');
      this.drawerReady = window.customElements && typeof window.customElements.whenDefined === 'function'
        ? window.customElements.whenDefined('side-drawer')
        : Promise.resolve();

      this.itemsContainer = this.drawer.querySelector('.js-favorites-items');
      this.emptyState = this.drawer.querySelector('.js-favorites-empty');
      this.titleCountTarget = this.drawer.querySelector('[data-favorites-count-text]');
      this.syncQueued = false;

      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleStorage = this.handleStorage.bind(this);
      this.handleDrawerOpen = this.handleDrawerOpen.bind(this);

      document.addEventListener('click', this.handleDocumentClick);
      window.addEventListener('storage', this.handleStorage);
      this.drawer.addEventListener('on:favorites-drawer:before-open', this.handleDrawerOpen);

      this.setupObserver();
      this.syncUi();
    }

    get canPersist() {
      return this.isCustomerLoggedIn && Boolean(this.storageKey);
    }

    getStorageKey() {
      if (!this.isCustomerLoggedIn || !this.customerKey) return '';
      return `${STORAGE_KEY_PREFIX}${this.customerKey}`;
    }

    setupObserver() {
      if (!('MutationObserver' in window)) return;

      this.observer = new MutationObserver((mutations) => {
        const hasFavoritesNodes = mutations.some((mutation) => {
          return Array.from(mutation.addedNodes).some((node) => {
            if (node.nodeType !== 1) return false;

            const matchesTrigger = typeof node.matches === 'function'
              ? node.matches('[data-favorites-toggle], [data-open-favorites], [data-favorites-count]')
              : false;
            const containsTrigger = typeof node.querySelector === 'function'
              ? node.querySelector('[data-favorites-toggle], [data-open-favorites], [data-favorites-count]')
              : false;

            return matchesTrigger || containsTrigger;
          });
        });

        if (hasFavoritesNodes) this.queueSync();
      });

      this.observer.observe(document.body, { childList: true, subtree: true });
    }

    queueSync() {
      if (this.syncQueued) return;
      this.syncQueued = true;
      window.requestAnimationFrame(() => {
        this.syncQueued = false;
        this.syncUi();
      });
    }

    read() {
      if (!this.canPersist) return [];

      try {
        const raw = window.localStorage.getItem(this.storageKey);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return this.sanitizeItems(parsed);
      } catch (error) {
        return [];
      }
    }

    write(items) {
      if (!this.canPersist) return;

      const nextItems = this.sanitizeItems(items).slice(0, MAX_ITEMS);

      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify(nextItems));
      } catch (error) {
        return;
      }

      this.syncUi();
    }

    normalizeItem(button) {
      return this.sanitizeItem({
        id: String(button.dataset.productId || '').trim(),
        handle: String(button.dataset.productHandle || '').trim(),
        url: String(button.dataset.productUrl || '').trim(),
        title: String(button.dataset.productTitle || '').trim(),
        image: String(button.dataset.productImage || '').trim(),
        imageAlt: String(button.dataset.productImageAlt || '').trim(),
        price: String(button.dataset.productPrice || '').trim()
      });
    }

    sanitizeItems(items) {
      if (!Array.isArray(items)) return [];

      const seen = new Set();
      const safeItems = [];

      items.forEach((item) => {
        const safeItem = this.sanitizeItem(item);
        if (!safeItem || seen.has(safeItem.id)) return;
        seen.add(safeItem.id);
        safeItems.push(safeItem);
      });

      return safeItems;
    }

    sanitizeItem(item) {
      if (!item || typeof item !== 'object') return null;

      const id = this.safeString(item.id, 32);
      const url = this.safeUrl(item.url);
      const title = this.safeString(item.title, 180);

      if (!id || !/^\d+$/.test(id) || !url || !title) return null;

      return {
        id,
        handle: this.safeString(item.handle, 120),
        url,
        title,
        image: this.safeImageUrl(item.image),
        imageAlt: this.safeString(item.imageAlt || title, 180),
        price: this.safeString(item.price, 120)
      };
    }

    has(productId) {
      return this.read().some((item) => item.id === String(productId));
    }

    toggle(button) {
      if (!this.canPersist) {
        this.openDrawer(button);
        return;
      }

      const item = this.normalizeItem(button);
      if (!item) return;

      const items = this.read();
      const existingIndex = items.findIndex((entry) => entry.id === item.id);
      const isAdding = existingIndex === -1;

      if (isAdding) {
        items.unshift(item);
      } else {
        items.splice(existingIndex, 1);
      }

      this.write(items.slice(0, MAX_ITEMS));
    }

    syncButtons() {
      const ids = new Set(this.read().map((item) => item.id));
      document.querySelectorAll('[data-favorites-toggle]').forEach((button) => {
        const active = this.canPersist && ids.has(String(button.dataset.productId));
        const label = active ? button.dataset.removeLabel : button.dataset.addLabel;
        const text = active ? button.dataset.savedText : button.dataset.addLabel;
        const loginRequiredLabel = button.dataset.loginRequiredLabel || this.drawer.dataset.loginRequiredAction || label;

        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
        button.setAttribute('aria-label', this.canPersist ? label : loginRequiredLabel);

        const textEl = button.querySelector('[data-favorites-toggle-text]');
        if (textEl) textEl.textContent = this.canPersist ? text : loginRequiredLabel;
      });
    }

    syncCounts() {
      const count = this.read().length;
      const text = count > 0 ? count : '';

      document.querySelectorAll('[data-favorites-count]').forEach((el) => {
        el.textContent = text;
      });

      if (this.titleCountTarget) {
        if (count < 1) {
          this.titleCountTarget.textContent = '';
        } else if (count === 1) {
          this.titleCountTarget.textContent = `(${this.drawer.dataset.countLabelOne})`;
        } else {
          this.titleCountTarget.textContent = `(${this.drawer.dataset.countLabelOther.replace('[count]', count)})`;
        }
      }
    }

    renderDrawer() {
      if (!this.itemsContainer || !this.emptyState) return;

      const items = this.read();
      if (!items.length) {
        this.itemsContainer.hidden = true;
        this.itemsContainer.innerHTML = '';
        this.emptyState.hidden = false;
        return;
      }

      this.emptyState.hidden = true;
      this.itemsContainer.hidden = false;
      this.itemsContainer.innerHTML = items.map((item) => {
        const title = this.escapeHtml(item.title);
        const image = item.image ? `<img src="${this.escapeAttribute(item.image)}" alt="${this.escapeAttribute(item.imageAlt || item.title)}" loading="lazy" width="160" height="200">` : '';
        const price = item.price ? `<div class="favorites-drawer__item-price">${this.escapeHtml(item.price)}</div>` : '';

        return `
          <article class="favorites-drawer__item">
            <a class="favorites-drawer__item-media" href="${this.escapeAttribute(item.url)}">
              ${image}
            </a>
            <div class="favorites-drawer__item-content">
              <a class="favorites-drawer__item-title" href="${this.escapeAttribute(item.url)}">${title}</a>
              ${price}
              <div class="favorites-drawer__item-actions">
                <a class="underline" href="${this.escapeAttribute(item.url)}">${this.escapeHtml(this.drawer.dataset.viewProductText)}</a>
                <button type="button" class="favorites-drawer__remove" data-favorites-remove="${this.escapeAttribute(item.id)}">${this.escapeHtml(this.drawer.dataset.removeText)}</button>
              </div>
            </div>
          </article>
        `;
      }).join('');
    }

    syncUi() {
      this.syncButtons();
      this.syncCounts();
      this.renderDrawer();
    }

    async openDrawer(opener) {
      if (!this.drawer) return;
      if (opener && opener.closest('.mobile-navigation-drawer')) {
        document.body.classList.remove('enable-mobile-nav-transition', 'reveal-mobile-nav', 'reveal-mobile-nav--revealed');
        if (window.removeTrapFocus && theme.trapOpener) removeTrapFocus(theme.trapOpener);
      }
      await this.drawerReady.catch(() => {});
      const focusTarget = this.drawer.querySelector('.drawer__close-btn');
      if (typeof this.drawer.open === 'function') {
        this.drawer.open(opener, focusTarget);
        return;
      }

      this.drawer.setAttribute('open', '');
      this.drawer.setAttribute('aria-hidden', 'false');
      if (this.overlay) this.overlay.classList.add('is-visible');
      if (focusTarget) focusTarget.focus();
    }

    remove(productId) {
      if (!this.canPersist) {
        this.openDrawer();
        return;
      }

      const nextItems = this.read().filter((item) => item.id !== String(productId));
      this.write(nextItems);
    }

    handleDrawerOpen() {
      this.renderDrawer();
    }

    handleStorage(evt) {
      if (evt.key === this.storageKey || evt.key === LEGACY_STORAGE_KEY) {
        this.syncUi();
      }
    }

    handleDocumentClick(evt) {
      const toggle = evt.target.closest('[data-favorites-toggle]');
      if (toggle) {
        evt.preventDefault();
        this.toggle(toggle);
        return;
      }

      const opener = evt.target.closest('[data-open-favorites]');
      if (opener) {
        evt.preventDefault();
        this.openDrawer(opener);
        return;
      }

      const removeButton = evt.target.closest('[data-favorites-remove]');
      if (removeButton) {
        evt.preventDefault();
        this.remove(removeButton.dataset.favoritesRemove);
      }
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    escapeAttribute(value) {
      return this.escapeHtml(value);
    }

    safeString(value, maxLength) {
      return String(value || '').replace(/\s+/g, ' ').trim().slice(0, maxLength);
    }

    safeUrl(value) {
      const raw = this.safeString(value, 1000);
      if (!raw) return '';

      try {
        const url = new URL(raw, window.location.origin);
        if (url.origin !== window.location.origin) return '';
        return `${url.pathname}${url.search}${url.hash}`;
      } catch (error) {
        return '';
      }
    }

    safeImageUrl(value) {
      const raw = this.safeString(value, 1000);
      if (!raw) return '';

      try {
        const url = new URL(raw, window.location.origin);
        if (url.origin === window.location.origin) return `${url.pathname}${url.search}${url.hash}`;
        if (url.protocol !== 'https:') return '';
        return url.href;
      } catch (error) {
        return '';
      }
    }
  }

  if (!window.themeFavorites) {
    window.themeFavorites = new FavoritesManager();
  }
})();
